import React, { createContext } from "react";
import {
  getUsername,
  setToken,
  clearTimers,
  extractRoleFromJWTToken,
} from "utils/Common";
import { withRouter } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import dayjs from "utils/dayjs";
import authClient from "authClient";
import store from "store";
import { uepClient } from "uepClient";
import { UserRoles } from "constants/UserConstants";
import { clearUserSession, setUserSession } from "actions/userSessionActions";
import { connect } from "react-redux";

export const TokenContext = createContext({
  authClient: null,
  handleTokenExpiry: () => Promise.resolve(),
});
class TokenHandler extends React.Component {
  constructor() {
    super();
    this.state = {
      isKeyCloakInitializing: true,
      isLoggingIn: false,
      loginError: "",
    };
  }
  componentDidMount() {
    this.initializeKeyCloakAndRedirect();
  }

  componentWillUnmount() {
    clearTimers([this.accessExpiryTimer, this.refreshTokenExpiryTimer]);
  }

  initializeKeyCloakAndRedirect = () => {
    authClient
      .init({
        onLoad: "login-required",
        promiseType: "native",
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .then((authenticated) => {
        if (authenticated) {
          this.setState({
            isKeyCloakInitializing: false,
            isLoggingIn: true,
          });
          this.updateStoreWithUserSession({
            token: authClient.token,
            refreshToken: authClient.refreshToken,
          });
          uepClient()
            .post(`/auth/login`)
            .then((res) => {
              const userData = res.data;
              const { username, firstName, lastName, group, channels } = userData;
              this.updateStoreWithUserSession({
                token: authClient.token,
                refreshToken: authClient.refreshToken,
                username: username,
                user: this.constructUserName(firstName, lastName),
                group: group,
              });
              this.onLoginDone();
              this.setState({
                isLoggingIn: false,
              });
              this.routeToPageBasedOnRoleAndChannel(extractRoleFromJWTToken(), channels);
              this.fetchGoogleApiToken();
            })
            .catch((err) => {
              console.error(err);
              this.setState({
                loginError:
                  "Invalid User! or failed to login, Please retry or contact UEP admin",
              });
              this.forceLogoutAfter10Secs();
            });
        }
      })
      .catch((err) => {
        console.error(err);
        this.setState({
          loginError:
            (err && err.message) ||
            "Failed to login, Please retry or contact UEP admin",
        });
        this.forceLogoutAfter10Secs();
      });
  };

  constructUserName = (firstName, lastName) => {
    if (!lastName) return firstName;
    if (!firstName) return lastName;
    return firstName + " " + lastName;
  };

  updateStoreWithUserSession = (currentSession) => {
    this.props.setUserSession(currentSession);
  };

  forceLogoutAfter10Secs = () => {
    setTimeout(() => {
      authClient.logout();
    }, 10 * 1000);
  };

  startTimerForAccessTokenExpiry = () => {
    this.accessExpiryTimer = setTimeout(
      this.refreshToken,
      this.getAccessTokenExpiryTime()
    );
  };

  startTimerForRefreshTokenExpiry = () => {
    this.refreshTokenExpiryTimer = setTimeout(
      this.refreshToken,
      this.getRefreshTokenExpiryTime()
    );
  };

  getRefreshTokenExpiryTime = () => {
    const expirytimeInUnixSeconds = authClient.refreshTokenParsed.exp;
    const currentTimeInUnixSeconds = dayjs().unix();
    const sixtySeconds = 60;
    const remainingTimeToRefreshInMS =
      (expirytimeInUnixSeconds - currentTimeInUnixSeconds - sixtySeconds) *
      1000;
    return remainingTimeToRefreshInMS;
  };

  render() {
    const { isKeyCloakInitializing, isLoggingIn, loginError } = this.state;
    return isKeyCloakInitializing || isLoggingIn ? (
      <>
        <Spinner />
        {loginError && (
          <h2 className="login-error" style={{ color: "red", padding: "15px" }}>
            {loginError}
          </h2>
        )}
      </>
    ) : (
      <TokenContext.Provider
        value={{ handleTokenExpiry: () => this.handleTokenExpiry, authClient }}
      >
        {this.props.children}
      </TokenContext.Provider>
    );
  }

  onLoginDone = () => {
    this.startTimerForAccessTokenExpiry();
    this.startTimerForRefreshTokenExpiry();
  };

  getAccessTokenExpiryTime = () => {
    const expirytimeInUnixSeconds = authClient.tokenParsed.exp;
    const currentTimeInUnixSeconds = dayjs().unix();
    const sixtySeconds = 60;
    const remainingTimeToRefreshInMS =
      (expirytimeInUnixSeconds - currentTimeInUnixSeconds - sixtySeconds) *
      1000;
    return remainingTimeToRefreshInMS;
  };

  refreshToken = () => {
    console.info(
      `userID: ${getUsername()}: calling refresh before token expiry at ${dayjs()}`
    );
    return new Promise((resolve, reject) => {
      authClient
        .updateToken(-1) // -1 for force refresh
        .then(() => {
          setToken(authClient.token);
          // The session reducer replaces state, so carry the rest of the session over. Without this
          // the socket (re)connect and log upload keep sending the expired login-time token.
          this.updateStoreWithUserSession({
            ...store.getState().userSession,
            token: authClient.token,
            refreshToken: authClient.refreshToken,
          });
          this.startTimerForAccessTokenExpiry();
          resolve(authClient.token);
          console.log('Token updated');
        })
        .catch((err) => {
          console.info(
            `Failed to refresh access token or refresh token expired for userID: ${getUsername()}, ${err}`
          );
          console.log('Im in catch block');
          this.logoutUser();
          reject(err);
        });
    });
  };

  handleTokenExpiry = () => {
    if (this.isAccessTokenExpired()) {
      return this.refreshToken();
    }
    return Promise.resolve(authClient.token);
  };

  isAccessTokenExpired = () => {
    const accessTokenToExpireInSeconds = this.getAccessTokenExpiryTime();
    return accessTokenToExpireInSeconds < 10;
  };

  routeToPageBasedOnRoleAndChannel = (role, channels) => {
    switch (role) {
      case UserRoles.AGENT:
        if (channels.includes("WHATSAPP_CV_SALES_BOT")) {
          return this.props.history.push("/sales-opportunities");
        }
        return this.props.history.push("/dashboard");
      case UserRoles.SUPERVISOR:
        return this.props.history.push("/supervisor");
      case UserRoles.ADMIN:
        return this.props.history.push("/admin");
      default:
        // role is not available in the token
        throw new Error("User role is invalid or role not defined for user");
    }
  };

  logoutUser = () => {
    uepClient()
      .post(`/auth/logout`, { username: getUsername() })
      .then(() => {
        this.props.clearUserSession();
        authClient.logout();
        this.props.history.push("/");
        window.location.href = "/";
      })
      .catch((error) => {
        console.error(
          "Some error occured while trying to log out.",
          error.message
        );
      });
  };

  fetchGoogleApiToken = () => {
    uepClient()
      .get(`/apikey`)
      .then((res) => {
        const encryptedApiKeys = { googleAPIKey: res.data.googleApiKey };
        window.apiKeys = encryptedApiKeys;
      })
      .catch((error) => {
        console.error(
          "Some error occured while trying to fetch Google api key.",
          error.message
        );
      });
  };
}

const mapDispatchToProps = {
  setUserSession: setUserSession,
  clearUserSession: clearUserSession,
};

export default withRouter(connect(null, mapDispatchToProps)(TokenHandler));